import { API_URL } from "src/utils/apiUrl";
import { jsonOrThrow, authHeaders } from "src/utils/apiFetch";

const j = (token) => ({ "Content-Type": "application/json", ...authHeaders(token) });

// ---- Vehicles ----
export async function listVehicles({ token, ownerAsiakasId }) {
  const res = await fetch(`${API_URL}/api/vehicle/list/${ownerAsiakasId}/-1`, { headers: authHeaders(token) });
  return jsonOrThrow(res);
}
export async function listVehicleTypes({ token, ownerAsiakasId }) {
  const res = await fetch(`${API_URL}/api/vehicle/listTypes/${ownerAsiakasId}`, { headers: authHeaders(token) });
  return jsonOrThrow(res);
}
export async function getVehicle({ token, vehicleId }) {
  const res = await fetch(`${API_URL}/api/vehicle/get/${vehicleId}`, { headers: authHeaders(token) });
  return jsonOrThrow(res); // recordset array; row is [0]
}
/**
 * Two-step vehicle create: allocate via vehicle/new, fetch the DB stub to get
 * required defaults, then save. Merges create-defaults (showInGrid, showInReports,
 * useNoDriverBar, isRestricted, hasGpsTracking) so the vehicle appears in the grid.
 * Rolls back via vehicle/delete if the save fails.
 *
 * `t` is optional (this module has no hook access, so the caller — VehiclesTab —
 * threads its own `useT()` result through) and only consulted on the error path;
 * callers that never hit it (tests, non-UI scripts) can omit it.
 */
export async function createVehicle({ token, ownerAsiakasId, fields, t }) {
  const created = await jsonOrThrow(
    await fetch(`${API_URL}/api/vehicle/new/${ownerAsiakasId}`, { method: "POST", headers: j(token) })
  );
  const vehicleId = created?.vehicleId;
  if (!vehicleId) throw new Error(t ? t("provider.hallintaApi.vehicleCreateFailed") : "vehicle/new ei palauttanut vehicleId:tä");
  try {
    const stub = (await getVehicle({ token, vehicleId }))?.[0] || {};
    return await saveVehicle({ token, fields: { ...stub, ...fields, vehicleId, asiakasId: ownerAsiakasId, showInGrid: true, showInReports: true, useNoDriverBar: true, isRestricted: false, hasGpsTracking: false } });
  } catch (err) {
    try { await deleteVehicle({ token, vehicleId }); } catch { /* best-effort cleanup */ }
    throw err;
  }
}
export async function saveVehicle({ token, fields }) {
  const res = await fetch(`${API_URL}/api/vehicle/save`, { method: "POST", headers: j(token), body: JSON.stringify(fields) });
  return jsonOrThrow(res);
}
/**
 * Set a vehicle's `lastDate` without touching deletedTime: retire by stamping a
 * date (the row stays so historical keikkas keep their reference), or reactivate
 * by passing `null`. Strips the list-only `__key`/`active` decoration before
 * saving the full vehicleView row back.
 */
export async function setVehicleLastDate({ token, vehicle, lastDate = null }) {
  const { __key: _k, active: _a, ...fields } = vehicle;
  return saveVehicle({ token, fields: { ...fields, lastDate } });
}
export async function deleteVehicle({ token, vehicleId }) {
  const res = await fetch(`${API_URL}/api/vehicle/delete/${vehicleId}`, { method: "POST", headers: j(token) });
  return jsonOrThrow(res);
}

// ---- Sijainnit ----
export async function listSijainnit({ token, asiakasId }) {
  const res = await fetch(`${API_URL}/api/asiakas/sijainti/list/${asiakasId}`, { headers: authHeaders(token) });
  return jsonOrThrow(res);
}
export async function listSijaintiTypes({ token, useJerry }) {
  const qs = useJerry ? "?useJerry=1" : "";
  const res = await fetch(`${API_URL}/api/geocode/sijaintiTypes${qs}`, { headers: authHeaders(token) });
  return jsonOrThrow(res);
}
export async function addSijainti({ token, fields }) {
  const res = await fetch(`${API_URL}/api/geocode/sijainti/add`, { method: "POST", headers: j(token), body: JSON.stringify(fields) });
  return jsonOrThrow(res);
}
export async function updateSijainti({ token, fields }) {
  const res = await fetch(`${API_URL}/api/geocode/updateSijainti`, { method: "POST", headers: j(token), body: JSON.stringify(fields) });
  return jsonOrThrow(res);
}
export async function deleteSijainti({ token, sijaintiId }) {
  const res = await fetch(`${API_URL}/api/geocode/sijainti/delete/${sijaintiId}`, { method: "DELETE", headers: authHeaders(token) });
  return jsonOrThrow(res);
}
/**
 * Write GPS coordinates for an existing sijainti. Called separately after
 * addSijainti/updateSijainti because those endpoints do not persist lat/lng.
 * Only invoked when the address picker captured valid finite coordinates.
 */
export async function updateSijaintiLatLng({ token, sijaintiId, lat, lng }) {
  const res = await fetch(`${API_URL}/api/geocode/updateLatLng/${sijaintiId}`, { method: "POST", headers: j(token), body: JSON.stringify({ lat, lng }) });
  return jsonOrThrow(res);
}

// ---- People + roles ----
export async function listPersons({ token, asiakasId }) {
  const res = await fetch(`${API_URL}/api/asiakas/person/list/${asiakasId}/0`, { headers: authHeaders(token) });
  return jsonOrThrow(res); // { personList, asiakasPersonSettings, ... }
}
export async function createPerson({ token, ownerAsiakasId, fields }) {
  const out = await jsonOrThrow(
    await fetch(`${API_URL}/api/person/newPerson`, { method: "POST", headers: j(token), body: JSON.stringify({ ...fields, ownerAsiakasId }) })
  );
  return out?.data?.returnValue ?? null; // new personId
}
export async function savePerson({ token, fields }) {
  const res = await fetch(`${API_URL}/api/person/set`, { method: "POST", headers: j(token), body: JSON.stringify(fields) });
  return jsonOrThrow(res);
}
export async function addRole({ token, asiakasId, personId, typeId }) {
  const res = await fetch(`${API_URL}/api/asiakasPersonSettings/add/${asiakasId}/${personId}/${typeId}`, { method: "POST", headers: j(token) });
  return jsonOrThrow(res);
}
export async function removeRole({ token, asiakasPersonSettingId }) {
  const res = await fetch(`${API_URL}/api/asiakasPersonSettings/delete/${asiakasPersonSettingId}`, { method: "DELETE", headers: authHeaders(token) });
  return jsonOrThrow(res);
}
export async function removePersonFromCompany({ token, asiakasId, personId }) {
  const res = await fetch(`${API_URL}/api/asiakas/person/remove`, { method: "POST", headers: j(token), body: JSON.stringify({ asiakasId, personId }) });
  return jsonOrThrow(res);
}
/**
 * Set/clear a person's ownerAsiakasId. `ownerAsiakasId: null` makes the person
 * GLOBAL (visible across all tenants). Server-side authz (canSetPersonOwner) applies:
 * a company-admin may release a person their company owns → global.
 *
 * `reason` is an internal changeTracker/X-Action-Reason audit annotation, not
 * UI copy rendered to any user — left untranslated deliberately (see the same
 * call in PeopleTab.jsx's doMakeGlobal).
 */
export async function setPersonOwner({ token, personId, ownerAsiakasId, reason = "Hallinta UI" }) {
  const res = await fetch(`${API_URL}/api/person/setOwner/${personId}`, {
    method: "POST",
    headers: { ...j(token), "X-Action-Reason": reason },
    body: JSON.stringify({ ownerAsiakasId }),
  });
  return jsonOrThrow(res);
}
export async function listRoleTypes({ token }) {
  const res = await fetch(`${API_URL}/api/asiakasPersonSettings/getAllTypes`, { headers: authHeaders(token) });
  return jsonOrThrow(res);
}

// ---- Jerry provider settings (Esittely + Jerry-asetukset) ----
export async function getJerryProviderSettings({ token, asiakasId }) {
  const res = await fetch(`${API_URL}/api/jerry-provider-settings?asiakasId=${asiakasId}`, { headers: authHeaders(token) });
  return jsonOrThrow(res); // { asiakasId, companyDescription, openingHours, jerryPersonName, ... }
}
export async function saveJerryProviderSettings({ token, asiakasId, fields }) {
  const res = await fetch(`${API_URL}/api/jerry-provider-settings`, {
    method: "PUT", headers: j(token), body: JSON.stringify({ asiakasId, ...fields }),
  });
  return jsonOrThrow(res);
}
